import React, { useState } from 'react';
import { Mail, MapPin, Send, Phone, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { socialLinks } from '../data/mock';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    toast.success('Message sent successfully! We\'ll get back to you soon.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="dark-container min-h-screen pt-28 pb-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="display-large text-white mb-4">Get In Touch</h1>
          <p className="body-large text-white/70 max-w-2xl mx-auto">
            Have questions? We're here to help. Reach out to our team anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {/* Contact Form */}
          <div className="md:col-span-2">
            <Card className="bg-white/5 border border-white/25 backdrop-blur-xl">
              <CardContent className="p-8">
                <h2 className="heading-1 text-white mb-6">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">Your Name *</label>
                      <Input
                        type="text"
                        placeholder="John Doe"
                        className="bg-white/5 border-white/25 text-white placeholder:text-white/40 h-12"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">Email Address *</label>
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        className="bg-white/5 border-white/25 text-white placeholder:text-white/40 h-12"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Subject</label>
                    <Input
                      type="text"
                      placeholder="How can we help?"
                      className="bg-white/5 border-white/25 text-white placeholder:text-white/40 h-12"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    />
                  </div>

                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Message *</label>
                    <Textarea
                      placeholder="Tell us more about your inquiry..."
                      className="bg-white/5 border-white/25 text-white placeholder:text-white/40 min-h-[150px]"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      required
                    />
                  </div>

                  <Button type="submit" className="btn-primary w-full md:w-auto">
                    Send Message
                    <Send size={18} />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <Card className="bg-white/5 border border-white/25 backdrop-blur-xl">
              <CardContent className="p-6">
                <h3 className="heading-2 text-white mb-6">Contact Information</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center flex-shrink-0">
                      <Mail className="text-brand-primary" size={18} />
                    </div>
                    <div>
                      <div className="text-white/60 text-sm mb-1">Email</div>
                      <a href="mailto:support@matriclaunch.com" className="text-white hover:text-brand-primary transition-colors">
                        support@matriclaunch.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-brand-primary" size={18} />
                    </div>
                    <div>
                      <div className="text-white/60 text-sm mb-1">Location</div>
                      <div className="text-white">
                        Global - Decentralized
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center flex-shrink-0">
                      <Phone className="text-brand-primary" size={18} />
                    </div>
                    <div>
                      <div className="text-white/60 text-sm mb-1">Support Hours</div>
                      <div className="text-white">24/7 Available</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border border-brand-primary backdrop-blur-xl">
              <CardContent className="p-6">
                <h3 className="heading-2 text-white mb-6">Join Our Community</h3>
                
                <div className="space-y-3">
                  <a 
                    href={socialLinks.telegram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-white/5 border border-white/25 rounded-sm hover:border-brand-primary transition-all duration-300"
                  >
                    <Send className="text-brand-primary" size={20} />
                    <div>
                      <div className="text-white font-medium text-sm">Telegram</div>
                      <div className="text-white/60 text-xs">Join our channel</div>
                    </div>
                  </a>

                  <a 
                    href={socialLinks.discord}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-white/5 border border-white/25 rounded-sm hover:border-brand-primary transition-all duration-300"
                  >
                    <MessageCircle className="text-brand-primary" size={20} />
                    <div>
                      <div className="text-white font-medium text-sm">Discord</div>
                      <div className="text-white/60 text-xs">Chat with community</div>
                    </div>
                  </a>

                  <a 
                    href={socialLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-white/5 border border-white/25 rounded-sm hover:border-brand-primary transition-all duration-300"
                  >
                    <svg className="text-brand-primary" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    <div>
                      <div className="text-white font-medium text-sm">Twitter / X</div>
                      <div className="text-white/60 text-xs">Follow for updates</div>
                    </div>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* FAQ Notice */}
        <div className="bg-white/5 border border-white/25 rounded-sm p-8 backdrop-blur-xl text-center">
          <h3 className="heading-2 text-white mb-3">Looking for Quick Answers?</h3>
          <p className="text-white/70 mb-6">
            Check out our FAQ section for instant answers to common questions
          </p>
          <Button className="btn-secondary" onClick={() => window.location.href = '/#faq'}>
            View FAQ
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Contact;
